import React, { useEffect, useMemo, useRef, useState } from "react";
import useSWR from "swr";
import { useCheckoutContext } from "lib/contexts/CheckoutProvider";
import { useLocale } from "lib/hooks/useLocale";
import { getRequest, kratosPublicFetcher, staticOptions } from "lib/core/apiClient";
import { useAuthContext } from "lib/contexts/AuthProvider";
import UpSellProductCard from "components/upsell/cards/UpSellProductCard";
import RecentlyViewedProductCard from "components/recentlyViewed/RecentlyViewedProductCard";
import * as Tab from "components/common/Tabs";
import { classNames } from "lib/utils/helpers";
import { getUpsellUrl, parseUpSellData, parseUpsellIds, parseUpsellWithProduct } from "lib/utils/upsell";
import {
  UpsellPositionType,
  UpSellProductType,
  UpsellAndWishlistGroupsOnDiscountType,
} from "lib/types/upsell";
import { RecentlyViewedProductType } from "lib/types/recentlyViewed";
import UpSellVariantSelectorDialog from "components/upsell/dialogs/UpSellVariantsSelectorDialog";
import useSendAnalyticsEvent from "lib/hooks/useAnalytics";
import { analyticsEvents, analyticsTypes } from "lib/utils/constants";
import { useWishlistContext } from "lib/contexts/WishlistProvider";
import { useRecentViewData } from "lib/hooks/useRecentlyViewed";
import WishListItemCard from "components/wishlist/WishlistItemCard";
import { WishlistItemType } from "lib/types/wishlist";
import { AnimateLoading } from "components/animation/AnimateLoading";
import UpsellSkeleton from "components/common/skeleton/UpsellSkeleton";
import { useToken } from "lib/contexts/TokenProvider";
import { useMerchantContext } from "lib/contexts/MerchantProvider";
import { ListCarousel, ListCarouselControls } from "@shopflo/ui/list-carousel";
import { UpsellConfigType } from "lib/types/merchantConfigTypes";
import { getCardBorderStyles } from "lib/utils/UIUtils";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { getTextColorByBrightness } from "@shopflo/ui/utils";
import { IconButton } from "@shopflo/ui/icon-button";

interface CustomTabConfig {
  value: string;
  title: string;
  products: UpSellProductType[];
}
interface UpsellSectionProps {
  parent?: UpsellPositionType;
  customTabs?: CustomTabConfig[];
  onUpsellDataParsed?: (data: {
    discountedUpsells: UpSellProductType[];
    nonDiscountedUpsells: UpSellProductType[];
  }) => void;
  onVisibilityChange?: (isVisible: boolean) => void;
  customisation?: UpsellConfigType;
}

const UpSellAndWishlistSection: React.FC<UpsellSectionProps> = React.memo(
  ({ parent, customTabs, onUpsellDataParsed, onVisibilityChange, customisation }) => {
    const { t } = useLocale();
    const {
      state: { tokenId, productType },
    } = useToken();
    const {
      state: { isAuthenticated, isLoggedInOnStore },
    } = useAuthContext();
    const {
      state: { checkoutId, checkoutItems },
    } = useCheckoutContext();
    const {
      state: { merchant, cartMetadata, checkoutMetadata },
    } = useMerchantContext();

    const wishlistConfig = merchant?.wishlistConfig;

    const { sendAnalyticsEvent } = useSendAnalyticsEvent();
    const {
      state: { count: wishlistedItemsCount, wishlistedItems },
    } = useWishlistContext();
    const {
      recentlyViewedProducts,
      isLoading: isRecentlyViewedDataLoading,
      removeRecentlyViewedProduct,
    } = useRecentViewData();

    const {
      upsell: cartUpsell,
      upsellPreview: cartUpsellPreview,
      recentlyViewedConfig,
    } = cartMetadata?.uiAttributes || {};
    const { upsell: checkoutUpsell, upsellPreview: checkoutUpsellPreview } =
      checkoutMetadata?.uiAttributes || {};
    const showPreview = parent === "CART" ? cartUpsellPreview : checkoutUpsellPreview;
    const upsell = parent === "CART" ? cartUpsell : checkoutUpsell;

    const [isVariantSelectorOpen, setIsVariantSelector] = useState<boolean>(false);
    const [selectedProduct, setSelectedProduct] = useState<UpSellProductType>();
    const [selectedTabTitle, setSelectedTabTitle] = useState<string>();
    const isEmptyCart = parent === "EMPTY_CART";
    const [activeTabValue, setActiveTabValue] = useState<string>("");
    const carouselControlsRef = useRef<Record<string, ListCarouselControls>>({});
    const [activeTabControls, setActiveTabControls] = useState<ListCarouselControls | undefined>();

    const registerCarouselControls = (tabValue: string, controls: ListCarouselControls) => {
      carouselControlsRef.current[tabValue] = controls;
      if (tabValue === activeTabValue) {
        setActiveTabControls(controls);
      }
    };

    const upsellUrl = getUpsellUrl({ checkoutId, tokenId });

    const {
      data: upsellResponse,
      isValidating,
      error,
    } = useSWR(
      () => {
        if (!upsellUrl || (!checkoutId && !tokenId)) return null;
        return upsellUrl;
      },
      (url: string) =>
        isAuthenticated && checkoutId ? getRequest(url, "KRATOS_PRIVATE") : kratosPublicFetcher(url),
      staticOptions,
    );

    // Empty cart always shows recently viewed; cart respects merchant config.
    const isRecentlyViewedEnabledForPage =
      parent === "EMPTY_CART" ||
      (Boolean(recentlyViewedConfig?.enabled) &&
        Boolean(recentlyViewedConfig?.page?.includes(parent as "CART" | "EMPTY_CART")));
    // Only gate the strip on recently-viewed loading when that tab can appear for this page.
    const isRecentlyViewedLoading = isRecentlyViewedEnabledForPage && isRecentlyViewedDataLoading;
    const isSectionLoading = isValidating || isRecentlyViewedLoading;

    // Useffect for passing data to parent
    useEffect(() => {
      if (error) {
        // const errorMsg = error?.response?.data?.error?.message;
        // errorToast(errorMsg ?? t("failed_to_fetch_upsells"));
        return;
      }
      if (!isValidating) {
        if (!Boolean(upsellResponse)) {
          onUpsellDataParsed?.({
            discountedUpsells: [],
            nonDiscountedUpsells: [],
          });
        } else {
          const parsedData = parseUpSellData(upsellResponse);
          onUpsellDataParsed?.(parsedData);
        }
      }
    }, [upsellResponse, isValidating, onUpsellDataParsed, error]);

    const { discountedUpsells, nonDiscountedUpsells } = useMemo(() => {
      if (!Boolean(upsellResponse)) {
        const emptyData = {
          discountedUpsells: [],
          nonDiscountedUpsells: [],
        };
        return emptyData;
      }
      return parseUpSellData(upsellResponse);
    }, [upsellResponse]);

    // Cart surfaces only. Checkout can run as a standalone page rather than
    // inside the cart iframe, so wishlist is not offered there.
    //
    // Both sessions must hold: isAuthenticated is this iframe's, isLoggedInOnStore
    // is the store's. Undefined means the storefront script has not answered yet,
    // so it stays hidden until it does rather than flashing in and out.
    const isWishlistEnabled = useMemo(() => {
      if (!["CART", "EMPTY_CART"].includes(parent ?? "")) return false;
      if (!isAuthenticated || !Boolean(isLoggedInOnStore)) return false;
      return Boolean(wishlistConfig?.isEnabled) && Boolean(wishlistConfig?.config?.cart?.enabled);
    }, [parent, isAuthenticated, isLoggedInOnStore, wishlistConfig]);

    const filteredWishlistedProducts = useMemo(() => {
      const checkoutItemsVariantIds = checkoutItems?.map((item) => item.variant_id);
      return wishlistedItems?.filter((item) => !checkoutItemsVariantIds.some((id) => id === item.variantId));
    }, [checkoutItems, wishlistedItems]);

    useEffect(() => {
      const loaderGroupIds = parseUpsellIds(upsellResponse);
      if (productType === "BACKGROUND_CART") return;
      if (!Boolean(loaderGroupIds.length)) return;
      sendAnalyticsEvent({
        eventName: analyticsEvents.UPSELL_WIDGET_LOADED,
        eventType: "load",
        eventFor: [analyticsTypes.SF_ANALYTICS],
        context: parent === "CART" || parent === "EMPTY_CART" ? "cart" : "checkout",
        metaData: {
          upsellData: {
            position: parent === "CART" || parent === "EMPTY_CART" ? "cart" : "payment_page",
            groupData: parseUpsellWithProduct(upsellResponse),
          },
        },
      });
    }, [upsellResponse, productType]);

    const openVariantSelector = (selectedProduct: UpSellProductType, tabTitle?: string) => {
      setSelectedProduct(selectedProduct);
      setSelectedTabTitle(tabTitle);
      setIsVariantSelector(true);
    };

    const closeVariantSelector = () => {
      setIsVariantSelector(false);
    };

    const recentlyViewedCardCustomisation: UpsellConfigType | undefined = useMemo(() => {
      if (!recentlyViewedConfig) return customisation;
      return {
        isEnabled: isEmptyCart || Boolean(recentlyViewedConfig.enabled),
        upsellTitle: customisation?.upsellTitle ?? "",
        discountedUpsellTitle: customisation?.discountedUpsellTitle ?? "",
        shouldOverrideDiscountedUpsell: customisation?.shouldOverrideDiscountedUpsell ?? false,
        clearUpsellItemsOnCartClose: customisation?.clearUpsellItemsOnCartClose ?? false,
        backgroundColor: recentlyViewedConfig.backgroundColor,
        borderColor: recentlyViewedConfig.borderColor,
        // Empty cart passes dark-aware text colors via customisation; prefer those over
        // the merchant recently-viewed config so name/price stay readable on dark pages.
        textColor: isEmptyCart
          ? customisation?.textColor ?? recentlyViewedConfig.textColor
          : recentlyViewedConfig.textColor,
        // For EMPTY_CART, the add button must always match the "Shop Now" CTA color,
        // regardless of the merchant's generic recently-viewed button color config.
        buttonColor: isEmptyCart
          ? customisation?.buttonColor ?? recentlyViewedConfig.buttonColor
          : recentlyViewedConfig.buttonColor,
        buttonTextColor: customisation?.buttonTextColor,
        offerBadgeColor: recentlyViewedConfig.offerBadgeColor,
        titleColor: isEmptyCart
          ? customisation?.titleColor ?? recentlyViewedConfig.titleColor
          : recentlyViewedConfig.titleColor,
        cardRadius: recentlyViewedConfig.cardRadius,
        cardBorderWidth: recentlyViewedConfig.cardBorderWidth,
        cardBorderSides: recentlyViewedConfig.cardBorderSides,
      };
    }, [recentlyViewedConfig, customisation, isEmptyCart]);

    const navButtonBackgroundColor =
      customisation?.backgroundColor && customisation.backgroundColor !== "transparent"
        ? customisation.backgroundColor
        : undefined;

    const controlButton = (
      <button
        className="ui-flex ui-h-6 ui-w-6 ui-items-center ui-justify-center ui-rounded-full ui-border-[1px] ui-border-carbon-lighter ui-bg-white ui-text-coal-dark"
        style={{
          backgroundColor: navButtonBackgroundColor,
          borderColor: getTextColorByBrightness({
            bgHexColor: navButtonBackgroundColor ?? "#FFFFFF",
            lightOpacity: 24,
          }),
          color: customisation?.textColor,
        }}>
        <CaretRight className="ui-h-4 ui-w-4" />
      </button>
    );

    const getTabConfig = () => {
      let tabs = [];
      const isCart = ["EMPTY_CART", "CART"].includes(parent ?? "");
      const discountedTitle = upsell?.discountedUpsellTitle;

      const nonDiscountedTitle = upsell?.upsellTitle;
      const recentlyViewedTitle = recentlyViewedConfig?.tabTitle;

      if (discountedUpsells.length) {
        tabs.push({
          value: UpsellAndWishlistGroupsOnDiscountType.DISCOUNTED,
          title: discountedTitle || t("best_offers"),
          content: (
            <div className="flex w-full flex-row items-center">
              <div className="flex w-full flex-col items-start justify-start space-y-2">
                <ListCarousel
                  count={discountedUpsells?.length}
                  customCSS="px-3"
                  shouldRenderDots={false}
                  navButton={controlButton}
                  hideNavButtons={isEmptyCart}
                  slideSpacing="gap"
                  onControlsChange={
                    isEmptyCart
                      ? (controls) =>
                          registerCarouselControls(UpsellAndWishlistGroupsOnDiscountType.DISCOUNTED, controls)
                      : undefined
                  }>
                  {discountedUpsells?.map((upSellProduct: UpSellProductType, index: number) => (
                    <UpSellProductCard
                      customisation={customisation}
                      isDiscounted={!upsell?.shouldOverrideDiscountedUpsell}
                      product={upSellProduct}
                      openVariantSelector={(p) => openVariantSelector(p, discountedTitle || t("best_offers"))}
                      key={`upsell-product${index + 1}`}
                      position={parent ? parent : "PAYMENT_PAGE"}
                      showPreview={showPreview}
                      isLast={index === discountedUpsells.length - 1}
                      tabTitle={discountedTitle || t("best_offers")}
                    />
                  ))}
                </ListCarousel>
              </div>
            </div>
          ),
        });
      }

      if (nonDiscountedUpsells.length) {
        tabs.push({
          value: UpsellAndWishlistGroupsOnDiscountType.NON_DISCOUNTED,
          title: nonDiscountedTitle || t("you_might_also_like"),
          content: (
            <div className="flex w-full flex-row items-center">
              <div className="flex w-full flex-col items-start justify-start space-y-2">
                <ListCarousel
                  count={nonDiscountedUpsells?.length}
                  customCSS="px-3"
                  shouldRenderDots={false}
                  navButton={controlButton}
                  hideNavButtons={isEmptyCart}
                  slideSpacing="gap"
                  onControlsChange={
                    isEmptyCart
                      ? (controls) =>
                          registerCarouselControls(
                            UpsellAndWishlistGroupsOnDiscountType.NON_DISCOUNTED,
                            controls,
                          )
                      : undefined
                  }>
                  {nonDiscountedUpsells?.map((upSellProduct: UpSellProductType, index: number) => (
                    <UpSellProductCard
                      customisation={customisation}
                      isDiscounted={false}
                      product={upSellProduct}
                      openVariantSelector={(p) =>
                        openVariantSelector(p, nonDiscountedTitle || t("you_might_also_like"))
                      }
                      key={`upsell-product${index + 1}`}
                      position={parent ? parent : "PAYMENT_PAGE"}
                      showPreview={showPreview}
                      isLast={index === nonDiscountedUpsells.length - 1}
                      tabTitle={nonDiscountedTitle || t("you_might_also_like")}
                    />
                  ))}
                </ListCarousel>
              </div>
            </div>
          ),
        });
      }

      if (
        isWishlistEnabled &&
        parent === "CART" &&
        wishlistedItemsCount > 0 &&
        Boolean(filteredWishlistedProducts?.length)
      ) {
        tabs.push({
          value: UpsellAndWishlistGroupsOnDiscountType.WISHLIST,
          title:
            parent === "CART"
              ? wishlistConfig?.config.cart.header || t("your_wishlist")
              : wishlistConfig?.config.checkout.header || t("your_wishlist"),
          content: (
            <div className="flex w-full flex-row items-center">
              <div className="flex w-full flex-col items-start justify-start space-y-2">
                <ListCarousel
                  count={filteredWishlistedProducts?.length}
                  customCSS="px-3"
                  shouldRenderDots={false}
                  navButton={controlButton}
                  hideNavButtons={isEmptyCart}
                  slideSpacing="gap"
                  onControlsChange={
                    isEmptyCart
                      ? (controls) =>
                          registerCarouselControls(UpsellAndWishlistGroupsOnDiscountType.WISHLIST, controls)
                      : undefined
                  }>
                  {filteredWishlistedProducts?.map((wishlistItem: WishlistItemType, index: number) => (
                    <WishListItemCard
                      key={`item-${index + 1}`}
                      index={index}
                      item={wishlistItem}
                      parent="UPSELL_STRIP"
                      customisation={customisation}
                    />
                  ))}
                </ListCarousel>
              </div>
            </div>
          ),
        });
      }

      // Empty cart always shows recently viewed; cart respects merchant config.
      const isRecentlyViewedEnabled =
        parent === "EMPTY_CART" ||
        (Boolean(recentlyViewedConfig?.enabled) &&
          Boolean(recentlyViewedConfig?.page?.includes(parent as "CART" | "EMPTY_CART")));
      const clampedRecentlyViewedProducts = recentlyViewedProducts.slice(
        0,
        recentlyViewedConfig?.maxItems ?? recentlyViewedProducts.length,
      );

      if (
        isRecentlyViewedEnabled &&
        clampedRecentlyViewedProducts.length >= (recentlyViewedConfig?.minItems ?? 1)
      ) {
        tabs.push({
          value: UpsellAndWishlistGroupsOnDiscountType.RECENTLY_VIEWED,
          title: recentlyViewedTitle || t("recently_viewed"),
          content: (
            <div className="flex w-full flex-row items-center">
              <div className="flex w-full flex-col items-start justify-start space-y-2">
                <ListCarousel
                  count={clampedRecentlyViewedProducts?.length}
                  customCSS="px-3"
                  shouldRenderDots={false}
                  navButton={controlButton}
                  hideNavButtons={isEmptyCart}
                  slideSpacing="gap"
                  onControlsChange={
                    isEmptyCart
                      ? (controls) =>
                          registerCarouselControls(
                            UpsellAndWishlistGroupsOnDiscountType.RECENTLY_VIEWED,
                            controls,
                          )
                      : undefined
                  }>
                  {clampedRecentlyViewedProducts?.map(
                    (recentlyViewedProduct: RecentlyViewedProductType, index: number) => (
                      <RecentlyViewedProductCard
                        // Active cart mirrors upsell strip styling; empty cart keeps its own treatment.
                        customisation={isEmptyCart ? recentlyViewedCardCustomisation : customisation}
                        product={recentlyViewedProduct}
                        key={recentlyViewedProduct.productId}
                        position={parent ? parent : "PAYMENT_PAGE"}
                        isLast={index === clampedRecentlyViewedProducts.length - 1}
                        onAddedToCart={removeRecentlyViewedProduct}
                      />
                    ),
                  )}
                </ListCarousel>
              </div>
            </div>
          ),
        });
      }

      if (customTabs?.length) {
        customTabs.forEach((customTab) => {
          tabs.push({
            value: customTab.value,
            title: customTab.title,
            content: (
              <div className="flex w-full flex-row items-center">
                <div className="flex w-full flex-col items-start justify-start space-y-2">
                  <ListCarousel
                    count={customTab.products?.length}
                    customCSS="px-3"
                    shouldRenderDots={false}
                    navButton={controlButton}
                    hideNavButtons={isEmptyCart}
                    slideSpacing="gap"
                    onControlsChange={
                      isEmptyCart
                        ? (controls) => registerCarouselControls(customTab.value, controls)
                        : undefined
                    }>
                    {customTab.products?.map((upSellProduct: UpSellProductType, index: number) => (
                      <UpSellProductCard
                        customisation={customisation}
                        isDiscounted={false}
                        product={upSellProduct}
                        openVariantSelector={(p) => openVariantSelector(p, customTab.title)}
                        key={`upsell-product${index + 1}`}
                        position={parent ? parent : "PAYMENT_PAGE"}
                        showPreview={showPreview}
                        isLast={index === customTab.products.length - 1}
                        tabTitle={customTab.title}
                      />
                    ))}
                  </ListCarousel>
                </div>
              </div>
            ),
          });
        });
      }

      if (upsell?.shouldOverrideDiscountedUpsell) {
        tabs = tabs.reverse();
      }

      return tabs;
    };

    const tabConfig = getTabConfig();

    useEffect(() => {
      if (isSectionLoading) {
        // Keep empty-cart gradient / skeleton mounted while upsell or recently-viewed
        // data is still resolving, so the strip doesn't pop in after a blank flash.
        onVisibilityChange?.(true);
        return;
      }
      onVisibilityChange?.(Boolean(tabConfig.length));
    }, [isSectionLoading, tabConfig.length, onVisibilityChange]);

    useEffect(() => {
      if (!isEmptyCart || !tabConfig.length) return;
      const isCurrentValueStillValid = tabConfig.some((tab) => tab.value === activeTabValue);
      if (isCurrentValueStillValid) return;
      const firstTabValue = tabConfig[0].value;
      setActiveTabValue(firstTabValue);
      setActiveTabControls(carouselControlsRef.current[firstTabValue]);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isEmptyCart, tabConfig, activeTabValue]);

    const handleTabSelect = (tabValue: string) => {
      setActiveTabValue(tabValue);
      setActiveTabControls(carouselControlsRef.current[tabValue]);
    };

    // This shitty code is written to handle the case when tabs are removed from the config
    // better approach is creatign empty state of wishlist tab instead of removing it
    const previousTabValuesRef = useRef<string[]>([]);
    const [tabResetKey, setTabResetKey] = useState(0);
    useEffect(() => {
      const currentTabValues = tabConfig.map((tab) => tab.value);
      const previousTabValues = previousTabValuesRef.current;

      // Check if any tabs were removed (existed before but don't exist now)
      const tabsWereRemoved = previousTabValues.some(
        (prevValue) => !currentTabValues.some((value) => value === prevValue),
      );

      if (tabsWereRemoved && previousTabValues.length > 0) {
        // Increment reset key to force Tab component remount
        setTabResetKey((prev) => prev + 1);
      }

      // Update ref with current values
      previousTabValuesRef.current = currentTabValues;
    }, [tabConfig]);

    if (!isSectionLoading && !tabConfig.length) return null;

    return (
      <div
        id="upsell-section"
        className={classNames("w-full", parent === "CART" || parent === "EMPTY_CART" ? "mt-0" : "mt-3")}>
        <AnimateLoading loading={isSectionLoading}>
          <AnimateLoading.Skeleton>
            <UpsellSkeleton />
          </AnimateLoading.Skeleton>
          <AnimateLoading.Content>
            <div
              className={classNames(
                "flex flex-col",
                parent === "EMPTY_CART" ? "" : "rounded-2xl border-zinc-200 bg-white",
              )}
              style={{
                backgroundColor: customisation?.backgroundColor,
                borderRadius: customisation?.cardRadius,
                ...getCardBorderStyles({
                  borderWidth: customisation?.cardBorderWidth,
                  borderSides: customisation?.cardBorderSides,
                  borderColor: customisation?.borderColor,
                }),
              }}>
              <div
                className={classNames(
                  "flex w-full flex-col",
                  parent === "EMPTY_CART" ? "gap-4" : "gap-3 pt-3",
                )}>
                <Tab.Root
                  key={tabResetKey}
                  defaultValue={tabConfig[0]?.value || UpsellAndWishlistGroupsOnDiscountType.DISCOUNTED}>
                  <div className={classNames(isEmptyCart ? "flex items-center justify-between" : "")}>
                    <Tab.List
                      className={classNames(
                        "flex items-start text-sm",
                        parent === "EMPTY_CART" ? "" : "mb-2 space-x-4 pl-3",
                      )}>
                      {tabConfig.map((tab) => (
                        <Tab.Trigger
                          key={tab.value}
                          value={tab.value}
                          renderTrigger={({ isSelected, onClick }) => (
                            <button
                              onClick={() => {
                                onClick();
                                handleTabSelect(tab.value);
                              }}
                              className={classNames(
                                "relative",
                                parent === "EMPTY_CART"
                                  ? "text-text-md-semibold"
                                  : isSelected
                                  ? "text-text-xs-semibold text-yay-dark"
                                  : "text-text-xs-semibold text-coal-light",
                              )}
                              style={{
                                color: isSelected ? customisation?.titleColor : customisation?.textColor,
                              }}>
                              {tab.title}
                              {Boolean(isSelected) && parent !== "EMPTY_CART" && (
                                <span
                                  className="absolute -bottom-2 left-0 w-full border-1 border-yay-dark"
                                  style={{
                                    borderColor: customisation?.titleColor,
                                  }}></span>
                              )}
                            </button>
                          )}
                        />
                      ))}
                    </Tab.List>
                    {isEmptyCart && (
                      <div className="flex items-center gap-1 mr-4">
                        <IconButton
                          variant="ghost"
                          size="sm"
                          IconComponent={CaretLeft}
                          iconProps={{ color: "#71717a" }}
                          disabled={!activeTabControls?.canScrollPrev}
                          onClick={() => activeTabControls?.scrollPrev()}
                        />
                        <IconButton
                          variant="ghost"
                          size="sm"
                          IconComponent={CaretRight}
                          iconProps={{ color: "#71717a" }}
                          disabled={!activeTabControls?.canScrollNext}
                          onClick={() => activeTabControls?.scrollNext()}
                        />
                      </div>
                    )}
                  </div>
                  {tabConfig.map((tab) => (
                    <Tab.Content key={tab.value} value={tab.value}>
                      {tab.content}
                    </Tab.Content>
                  ))}
                </Tab.Root>
              </div>
              <UpSellVariantSelectorDialog
                upsellProduct={selectedProduct}
                isVariantSelectorOpen={isVariantSelectorOpen}
                closeVariantSelector={closeVariantSelector}
                position={parent ? parent : "PAYMENT_PAGE"}
                onPreviousClick={closeVariantSelector}
                tabTitle={selectedTabTitle}
              />
            </div>
          </AnimateLoading.Content>
        </AnimateLoading>
      </div>
    );
  },
);

export default UpSellAndWishlistSection;
